import { BaseProvider } from "./base.provider";

export interface StorageObject {
  key: string;
  url: string;
  size: number;
  last_modified: string;
}

export interface StorageListing {
  prefix: string;
  dirs: string[];
  objects: StorageObject[];
}

export interface StorageFlatListing {
  objects: StorageObject[];
  next_page_token?: string;
}

export interface StorageUploadUrl {
  upload_url: string;
  form_fields: Record<string, string>;
  key: string;
  url: string;
}

class StorageProvider extends BaseProvider {
  list(prefix = ""): Promise<StorageListing> {
    const params = new URLSearchParams();
    if (prefix) params.set("prefix", prefix);
    const qs = params.toString();
    return this.request<StorageListing>(
      `/v1/admin/storage/list${qs ? `?${qs}` : ""}`,
    );
  }

  listAll(
    prefix = "",
    pageToken?: string,
    pageSize = 100,
  ): Promise<StorageFlatListing> {
    const params = new URLSearchParams();
    if (prefix) params.set("prefix", prefix);
    if (pageToken) params.set("page_token", pageToken);
    if (pageSize) params.set("page_size", String(pageSize));
    const qs = params.toString();
    return this.request<StorageFlatListing>(
      `/v1/admin/storage/list-all${qs ? `?${qs}` : ""}`,
    );
  }

  getUploadUrl(prefix: string, filename: string): Promise<StorageUploadUrl> {
    return this.request<StorageUploadUrl>("/v1/admin/storage/upload-url", {
      method: "POST",
      body: JSON.stringify({ prefix, filename }),
    });
  }

  async uploadToPresignedUrl(
    upload: StorageUploadUrl,
    file: File,
  ): Promise<void> {
    const form = new FormData();
    for (const [key, value] of Object.entries(upload.form_fields)) {
      form.append(key, value);
    }
    form.append("Content-Type", file.type);
    form.append("file", file);

    const res = await fetch(upload.upload_url, { method: "POST", body: form });
    if (!res.ok) {
      const text = await res.text().catch(() => "Unknown error");
      throw new Error(text || `Upload failed: HTTP ${res.status}`);
    }
  }

  delete(key: string): Promise<void> {
    return this.request<void>("/v1/admin/storage", {
      method: "DELETE",
      body: JSON.stringify({ key }),
    });
  }
}

export const storageProvider = new StorageProvider();
